import { chakra } from '@chakra-ui/react'
import type { FC } from 'react'
import type { ComponentWithAs, IconProps } from '@chakra-ui/react'
import {
  LuHouse,
  LuUsers,
  LuVideo,
  LuMicroscope,
  LuGraduationCap,
  LuShieldCheck,
  LuActivity,
  LuBookOpen
} from 'react-icons/lu'

import { ModuleType, IModuleConfig } from '../types/modules'

// Translation keys for module labels
export const MODULE_LABEL_KEYS: Record<ModuleType, string> = {
  [ModuleType.HOME]: 'modules.home',
  [ModuleType.PATIENTS]: 'modules.patients',
  [ModuleType.BLOT_ANALYSIS]: 'modules.blotAnalysis',
  [ModuleType.TELEEXPERTISE]: 'modules.teleexpertise',
  [ModuleType.LAB]: 'modules.lab',
  [ModuleType.LITERATURE_WATCH]: 'modules.literatureWatch',
  [ModuleType.TRAINING]: 'modules.training',
  [ModuleType.ADMIN]: 'modules.admin',
}

const ChakraLuHouse: ComponentWithAs<'svg', IconProps> = chakra(LuHouse as FC)
const ChakraLuUsers: ComponentWithAs<'svg', IconProps> = chakra(LuUsers as FC)
const ChakraLuVideo: ComponentWithAs<'svg', IconProps> = chakra(LuVideo as FC)
const ChakraLuMicroscope: ComponentWithAs<'svg', IconProps> = chakra(LuMicroscope as FC)
const ChakraLuGraduationCap: ComponentWithAs<'svg', IconProps> = chakra(LuGraduationCap as FC)
const ChakraLuShieldCheck: ComponentWithAs<'svg', IconProps> = chakra(LuShieldCheck as FC)
const ChakraLuActivity: ComponentWithAs<'svg', IconProps> = chakra(LuActivity as FC)
const ChakraLuBookOpen: ComponentWithAs<'svg', IconProps> = chakra(LuBookOpen as FC)

export const MODULE_CONFIGS: Record<ModuleType, IModuleConfig> = {
  [ModuleType.HOME]: {
    type: ModuleType.HOME,
    label: 'Home',
    icon: <ChakraLuHouse boxSize={6} strokeWidth={2} />,
    href: '/home',
    ariaLabel: 'home',
    description: 'Dashboard and overview'
  },
  [ModuleType.PATIENTS]: {
    type: ModuleType.PATIENTS,
    label: 'Patients',
    icon: <ChakraLuUsers boxSize={6} strokeWidth={2} />,
    href: '/patients',
    ariaLabel: 'patients',
    description: 'Patient management'
  },
  [ModuleType.BLOT_ANALYSIS]: {
    type: ModuleType.BLOT_ANALYSIS,
    label: 'IRIS',
    icon: <ChakraLuActivity boxSize={6} strokeWidth={2} />,
    href: '/blot-analysis',
    ariaLabel: 'blot-analysis',
    description: 'Automated Western Blot analysis with intelligent band detection',
    hideFromSidebar: true // Accessed via LAB module, not directly in sidebar
  },
  [ModuleType.TELEEXPERTISE]: {
    type: ModuleType.TELEEXPERTISE,
    label: 'Téléexpertise',
    icon: <ChakraLuVideo boxSize={6} strokeWidth={2} />,
    href: '/myCollaboratory',
    ariaLabel: 'teleexpertise',
    description: 'Collaboration et consultation à distance entre experts',
    hideFromSidebar: true
  },
  [ModuleType.LAB]: {
    type: ModuleType.LAB,
    label: 'AI Applications',
    icon: <ChakraLuMicroscope boxSize={6} strokeWidth={2} />,
    href: '/lab',
    ariaLabel: 'lab',
    description: 'AI-powered medical diagnostic applications for various specialties'
  },
  [ModuleType.LITERATURE_WATCH]: {
    type: ModuleType.LITERATURE_WATCH,
    label: 'MedInsight',
    icon: <ChakraLuBookOpen boxSize={6} strokeWidth={2} />,
    href: '/literature',
    ariaLabel: 'medinsight',
    description: 'Hub intelligent de veille scientifique médicale - Recherchez, organisez et annotez la littérature',
    hideFromSidebar: true
  },
  [ModuleType.TRAINING]: {
    type: ModuleType.TRAINING,
    label: 'Training',
    icon: <ChakraLuGraduationCap boxSize={6} strokeWidth={2} />,
    href: '/training',
    ariaLabel: 'training',
    description: 'Educational resources and training'
  },
  [ModuleType.ADMIN]: {
    type: ModuleType.ADMIN,
    label: 'Admin',
    icon: <ChakraLuShieldCheck boxSize={6} strokeWidth={2} />,
    href: '/admin',
    ariaLabel: 'admin',
    description: 'Administrative settings',
    requiredRole: ['admin', 'super_admin']
  }
}

export const getAvailableModules = (userModules: string[], userRole?: string): IModuleConfig[] => {
  return Object.values(MODULE_CONFIGS).filter(moduleConfig => {
    // Skip modules hidden from sidebar (accessed via parent modules)
    if (moduleConfig.hideFromSidebar) {
      return false
    }

    // HOME module is always visible for everyone
    if (moduleConfig.type === ModuleType.HOME) {
      return true
    }

    // ADMIN module is visible only if user has admin/super_admin role
    if (moduleConfig.type === ModuleType.ADMIN) {
      return userRole && ['admin', 'super_admin'].includes(userRole)
    }

    // For other modules, check if user has this module enabled
    const hasModule = userModules.includes(moduleConfig.type)

    // Check role requirements
    const hasRoleAccess = !moduleConfig.requiredRole ||
      (userRole && moduleConfig.requiredRole.includes(userRole))

    return hasModule && hasRoleAccess
  })
}
